import { CreateBaggageCarouselButton } from "@/components/baggage/CreateBaggageCarouselButton";
import { UpdateBaggageCarouselDialog } from "@/components/baggage/UpdateBaggageCarouselDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useBaggageCarousels,
  useBaggageHalls,
  useDeleteBaggageCarousel,
} from "@/queries/baggage";
import { AlertCircle, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

interface BaggageCarouselItemProps {
  carousel: any;
  baggageHallName: string;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

const BaggageCarouselItem: React.FC<BaggageCarouselItemProps> = ({
  carousel,
  baggageHallName,
  onDelete,
  isDeleting,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <div className="border rounded-md p-4 space-y-1 relative">
        <div className="absolute top-2 right-2 flex gap-2">
          <UpdateBaggageCarouselDialog carousel={carousel} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap md:flex-nowrap items-center gap-4">
          <div className="flex-1 space-y-1">
            <h3 className="font-semibold text-lg mr-20">{carousel.name}</h3>
            <p className="text-sm text-muted-foreground">
              Baggage Hall: {baggageHallName}
            </p>
            <p className="text-sm">
              <strong>Identifiers:</strong>{" "}
              {carousel.identifiers?.length > 0 ? (
                <span className="inline-flex flex-wrap gap-1 mt-1">
                  {carousel.identifiers.map((id: string) => (
                    <span
                      key={id}
                      className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                    >
                      {id}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-muted-foreground">None</span>
              )}
            </p>
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm">
              <strong>Preferred Carriers:</strong>{" "}
              {carousel.preferred_carriers?.length > 0 ? (
                <span className="inline-flex flex-wrap gap-1 mt-1">
                  {carousel.preferred_carriers.map((code: string) => (
                    <Badge key={code} variant="secondary">
                      {code}
                    </Badge>
                  ))}
                </span>
              ) : (
                <span className="text-muted-foreground">None</span>
              )}
            </p>
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm text-muted-foreground">
              <strong>Created:</strong>{" "}
              {new Date(carousel.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Baggage Carousel</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{carousel.name}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(carousel.id);
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function BaggageCarousels() {
  const {
    data: carouselsData,
    isLoading: isLoadingCarousels,
    error: errorCarousels,
  } = useBaggageCarousels();

  const { data: baggageHalls } = useBaggageHalls();

  const deleteMutation = useDeleteBaggageCarousel();

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const baggageHallMap = useMemo(() => {
    if (!baggageHalls) return {};
    return baggageHalls.reduce(
      (acc, hall) => {
        acc[hall.id] = hall.name;
        return acc;
      },
      {} as Record<number, string>
    );
  }, [baggageHalls]);

  const sortedCarousels = useMemo(() => {
    if (!carouselsData) return [];

    return [...carouselsData].sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [carouselsData]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Baggage Carousels</CardTitle>
          <CardDescription>
            Manage baggage claim carousels and their configurations.
          </CardDescription>
        </div>
        <CreateBaggageCarouselButton />
      </CardHeader>
      <CardContent>
        {isLoadingCarousels && <p>Loading baggage carousels...</p>}
        {errorCarousels && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <p>Error loading baggage carousels: {errorCarousels.message}</p>
          </div>
        )}
        {!isLoadingCarousels && !errorCarousels && (
          <div className="space-y-4">
            {sortedCarousels.length > 0 ? (
              sortedCarousels.map((carousel) => (
                <BaggageCarouselItem
                  key={carousel.id}
                  carousel={carousel}
                  baggageHallName={
                    baggageHallMap[carousel.baggage_hall_id] ||
                    `ID: ${carousel.baggage_hall_id}`
                  }
                  onDelete={handleDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground">
                No baggage carousels found. Click "Add Carousel" to create one.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
